import { isArray, isEmpty, size } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import FileLoading from '../../PdcSupportingDocuments/components/UploadMultipleFile/FileLoading';
import {
  DragDropText,
  DragDropTextLimit,
  FileUploadContainer,
  FormField,
  UploadFileBtn,
  UploadIcon,
} from '../../../Meeting/components/FileUpload/Styled';
import { upload_icon } from '../../../../assets/img';
import FileUploaded from '../../PdcSupportingDocuments/components/UploadMultipleFile/FileUploaded';
import { PAGE_NAME } from '../../../../utils/constants';

const LIMIT_FILE_SIZE = 1000000000;
const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 1024 * 1024 * LIMIT_FILE_SIZE;

let tempPrevFileLoading = [];

const FileUploadPDC = ({
  setFileUpload,
  setFileUploadStatus,
  fileUploadStatus,
  setFileSizeUpload,
  fileUpload,
  maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
  isClickedSave,
  dataFileUploaded,
  ...otherProps
}) => {
  const fileInputField = useRef(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState([]);

  const handleUploadBtnClick = () => {
    fileInputField.current.click();
  };

  const handleCheckFile = (file) => {
    if (file && file.size > maxFileSizeInBytes) {
      setFileSizeUpload(file.size);
      setFileUploadStatus((prev) => ({ ...prev, limitFileSize: true }));
      return true;
    }
    if (file?.name && !file.name.toLowerCase().includes('.pdf')) {
      setFileUploadStatus((prev) => ({ ...prev, notPdfFile: true }));
      return true;
    }
    return false;
  };

  const checkAllFileValid = (files) => {
    let isFileValid = false;
    for (let i = 0; i < files.length; i++) {
      const fileElem = files[i];
      isFileValid = handleCheckFile(fileElem);
      if (isFileValid) {
        return true;
      }
    }
    return isFileValid;
  };

  const handleAddNewFiles = (e) => {
    setFileUploaded([]);
    const { files: newFiles } = e.target;
    if (newFiles.length <= 0) {
      return;
    }

    const isCheck = checkAllFileValid(newFiles);
    if (!isCheck) {
      let tempData = [];
      for (let i = 0; i < newFiles.length; i++) {
        const fileElem = newFiles[i];
        tempData.push({ fileData: fileElem, tempId: uuidv4() });
      }
      if (isEmpty(tempPrevFileLoading)) {
        tempPrevFileLoading = tempData.map((item) => ({ ...item, isLoading: true }));
      } else {
        tempPrevFileLoading = [
          ...tempPrevFileLoading.map((i) => ({ ...i, isLoading: false })),
          ...tempData.map((f) => {
            let tempIndex = tempPrevFileLoading.findIndex((item) => item?.tempId === f?.tempId);
            if (tempIndex !== -1) {
              return { ...f, isLoading: false };
            } else {
              return { ...f, isLoading: true };
            }
          }),
        ];
      }
    }
  };

  const handleSetFileUploaded = (record) => {
    tempPrevFileLoading = tempPrevFileLoading
      .filter((i) => !i?.isDeletedMoM)
      .map((prevItem) => {
        if (prevItem?.tempId === record?.tempId) {
          return record;
        } else {
          return prevItem;
        }
      });
  };

  const getContentFileUploading = (files) => {
    return files
      .filter((i) => !i?.loaded)
      .map((item) => (
        <FileLoading
          fileItem={item}
          key={item?.tempId || item?.id}
          setFileUploadStatus={setFileUploadStatus}
          setIsFileLoading={setIsFileLoading}
          setFileUploaded={setFileUploaded}
          fileUploadStatus={fileUploadStatus}
          isFileLoading={isFileLoading}
          handleSetFileUploaded={handleSetFileUploaded}
          pageName={PAGE_NAME.PDC_MEETING}
        />
      ));
  };

  useEffect(() => {
    if (!isEmpty(fileUploaded)) {
      setFileUpload((prev) => {
        const tempFiles = [];
        if (!isEmpty(fileUploaded)) {
          fileUploaded.forEach((f) => {
            const isCheck = !isEmpty(prev) ? fileUpload.filter((i) => i?.id === f?.id).length > 0 : false;
            if (!isCheck) {
              tempFiles.push(f);
            }
          });
        }
        return isArray(prev) ? [...prev, ...tempFiles] : [...tempFiles];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploaded, setFileUpload]);

  // Handle reset temporary files
  useEffect(() => {
    if (!isEmpty(fileUploaded) && !isEmpty(tempPrevFileLoading) && size(fileUploaded) === size(tempPrevFileLoading)) {
      tempPrevFileLoading = [];
    }
  }, [fileUploaded]);

  const isCheckUploadFileStatus = useMemo(() => {
    return fileUploadStatus.hasError || fileUploadStatus.limitFileSize || fileUploadStatus.notPdfFile || fileUploadStatus.maximumOf5Files;
  }, [fileUploadStatus.hasError, fileUploadStatus.limitFileSize, fileUploadStatus.maximumOf5Files, fileUploadStatus.notPdfFile]);

  return (
    <>
      {!isFileLoading && isEmpty(dataFileUploaded) && !fileUploadStatus.hasError && (
        <FileUploadContainer error={(isClickedSave && isEmpty(fileUpload)) || isCheckUploadFileStatus} style={{ borderColor: '#26afab' }}>
          <UploadIcon src={upload_icon} alt=""></UploadIcon>
          <DragDropText>Drag and drop your file here</DragDropText>
          {otherProps?.pageContent && (
            <>
              <DragDropTextLimit>
                Limit {otherProps?.pageContent?.fileLimit} per file <br />
                {otherProps?.pageContent?.uploadEx} <br />
                <strong>OR</strong>
              </DragDropTextLimit>
            </>
          )}
          <UploadFileBtn type="button" onClick={handleUploadBtnClick} data-testid="fileUploadBtn">
            <span>Browse File</span>
          </UploadFileBtn>
          <FormField type="file" name="files" ref={fileInputField} onChange={handleAddNewFiles} title="" value="" multiple={false} {...otherProps} />
        </FileUploadContainer>
      )}
      {(tempPrevFileLoading.length > 0 || fileUploadStatus.hasError) && <>{getContentFileUploading(tempPrevFileLoading)}</>}
      {isArray(dataFileUploaded) &&
        dataFileUploaded.map((fileItem, index) => (
          <FileUploaded
            key={index}
            fileItem={fileItem}
            setFileUploadStatus={setFileUploadStatus}
            setFileUpload={setFileUpload}
            isUploading={isFileLoading}
            fileUploadStatus={fileUploadStatus}
            pageName={PAGE_NAME.PDC_MEETING}
          />
        ))}
    </>
  );
};

export default FileUploadPDC;
